export type PostStatus = 'pending' | 'live' | 'held' | 'removed';
export type AuthorKind = 'human' | 'agent';

export interface LatLng {
  lat: number;
  lng: number;
}

/** Image references are Storage object paths; clients turn them into URLs (see storagePathToUrl). */
export interface PostImage {
  full: string;
  thumb: string;
  w: number;
  h: number;
}

/**
 * Firestore `posts/{id}`. Timestamps are Firestore Timestamps, whose class differs between the web
 * SDK and the Admin SDK, hence the type parameter.
 */
export interface PostDoc<TTime = unknown> {
  title: string;
  body: string;
  links: string[];
  images: PostImage[];
  authorId: string;
  authorName: string;
  authorPhoto: string | null;
  authorKind: AuthorKind;
  /** Geohash (precision 5) of the poster's ~5 km area, or null for a World-only post. */
  g5: string | null;
  status: PostStatus;
  reportCount: number;
  /** Set when moderation flagged the post or skipped scanning. */
  moderation: 'clean' | 'flagged' | 'unscanned' | null;
  createdAt: TTime;
  liveAt: TTime | null;
}

/** The small, denormalized view of a post shown on boards. */
export interface TileSummary {
  id: string;
  title: string;
  /** Start of the description (see EXCERPT_MAX), shown on text tiles. */
  excerpt: string;
  /** Storage path of the thumbnail, or null. */
  thumb: string | null;
  author: string;
  isAgent: boolean;
  g5: string | null;
  /** Epoch ms. */
  createdAt: number;
}

export interface AreaTile extends TileSummary {
  /** Hot score as of the area doc's `rankedAt`. */
  score: number;
}

/**
 * Firestore `areas/{key}`: a precomputed board for one geohash cell (or `world`). Two writers update
 * disjoint fields: `rankBoard` writes `tiles`/`rankedAt`, `onPostCreated` writes `latest`/`latestAt`.
 */
export interface AreaDoc {
  key: string;
  tiles: AreaTile[];
  rankedAt: number;
  latest: TileSummary[];
  latestAt: number;
}

/** Firestore `opens/{postId}_{uid}_{utcDay}`, created by clients when a post is opened. */
export interface OpenEvent {
  postId: string;
  uid: string;
  anon: boolean;
  /** Epoch ms. */
  at: number;
}

export interface TrackedPost extends TileSummary {
  score: number;
  /** Epoch ms at which `score` was last brought up to date. */
  scoredAt: number;
}

export interface Cursor {
  at: number;
  id: string;
}

/** Firestore `ranking/state`: private state of the ranking job. */
export interface RankingState {
  version: 1;
  postsCursor: Cursor | null;
  opensCursor: Cursor | null;
  posts: Record<string, TrackedPost>;
  areaHashes: Record<string, string>;
  updatedAt: number;
}

/** Input accepted by the `createPost` callable. */
export interface CreatePostInput {
  title: string;
  body: string;
  links: string[];
  images: PostImage[];
  g5: string | null;
}

export type AdminAction = 'hide' | 'restore' | 'ban' | 'unban';

/** What the admin's stats show (the `adminStats` callable). */
/** How far back the admin's stats look, in days (null: since the start). */
export type StatsRange = 7 | 30 | 90 | null;
export const STATS_RANGES: readonly StatsRange[] = [7, 30, 90, null];
export const DEFAULT_STATS_RANGE: StatsRange = 30;

/**
 * What the admin's stats say about real people over a range: visitors (each browser is one,
 * the admins' own left out; automated browsers never get counted), by country, and posts.
 */
export interface AppStats {
  generatedAt: number;
  range: StatsRange;
  people: {
    /** Seen in the range. */
    visited: number;
    /** Of those, came back on a later day than their first visit: surely real people. */
    regulars: number;
    /** First visit in the range. */
    newcomers: number;
    /** Signed in with Google. */
    signedIn: number;
    /** Too many accounts to count them all: the numbers are a floor. */
    capped: boolean;
  };
  /** People seen in the range by country (ISO code; null: not known), most first. */
  countries: Array<{ country: string | null; people: number; regulars: number }>;
  /** Posts made in the range, by people and by the AI news desk. */
  posts: { people: number; ai: number };
}
